import { useCallback, useEffect, useState, useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';
import { useSelector, useDispatch } from 'react-redux';

import { fetchDynamicSenderProjectSetting } from 'api/queries';
import { MESSAGE_TYPES_AND_SENDERS_URL } from 'api/TemplateIndexAPI';
import { selectUserProjectId } from 'core/userContext';
import { ApiResponse, getRaw } from 'services/api';
import { FormField } from 'ui/formField';
import {
  BaseOption,
  BasicFilterSelect,
  BasicSelectProps,
  OnSelectHandler,
  SelectProps,
  TypeaheadBasicSelectProps,
  TypeaheadSelect,
} from 'ui/selects';
import { addNegativeToast } from 'modules/toastContainer';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { FieldNames, MediumDataKey } from 'modules/templateEditor/common/types';
import { EmailContentDefinition, MessageMedium } from 'modules/graphql/types';
import { SettingsReadOnlyText } from 'modules/templateEditor/common';
import { useTranslations } from 'modules/templateEditor/hooks/useTranslations';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { constructErrorInlineText } from 'modules/templateEditor/common/ErrorInlineText';
import { constructFieldErrors, getIsEditMode } from 'modules/templateEditor/common/utils';

type AuthEmailType = {
  authorized: boolean;
  defaultReplyTo?: string;
  email: string;
  senderName?: string;
};

type SendersDataType = {
  data?: {
    emailSenders?: AuthEmailType[];
  };
};

const getMessageTypesAndSenders = (
  projectId: number,
  messageMedium: MessageMedium,
): Promise<ApiResponse> => {
  return getRaw(
    `${MESSAGE_TYPES_AND_SENDERS_URL}?projectId=${projectId}&messageMedium=${messageMedium}`,
  );
};

const fromEmailMediumDataKey: MediumDataKey = 'fromEmail';
const fromNameMediumDataKey: MediumDataKey = 'fromName';
const replyToMediumDataKey: MediumDataKey = 'replyTo';

const getHelperText = (isEditMode: boolean, isDynamicSenderEnabled: boolean) => {
  if (!isEditMode) return '';
  return isDynamicSenderEnabled
    ? 'message.forEmailDynamicSenderHelpText'
    : 'message.forEmailHelpText';
};

export const FromEmailField = () => {
  const { t } = useTranslations();
  const { view } = useQueryParamContext();
  const dispatch = useDispatch();
  const [emailSenders, setEmailSenders] = useState<BaseOption<string>[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const { liveTemplate, setCurrentMediumFields, isEditMode } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const emailData = (liveTemplate?.mediumData || {}) as EmailContentDefinition;
  const projectId = useSelector(selectUserProjectId);

  // The useDynamicSenders project setting will decide if the user can free
  // type in the 'From email' input field.
  const { data = {} } = useGraphql(fetchDynamicSenderProjectSetting, {});
  const { useDynamicSenders } = data?.currentProject || {};

  const { hasError: hasFromEmailError } = constructFieldErrors({
    contentWarnings: localeContentErrors,
    contentErrors: localeContentWarnings,
    fieldName: FieldNames.FROM_EMAIL,
  });
  const fromEmailErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasFromEmailError,
  });

  /**
   * Handle input field changes and also update the 'From Name'
   * and the 'Reply to email' field based on the selected option.
   * If, they have been configured while registering a sender.
   */
  const onSelect: OnSelectHandler = ([selected], { selectedOptions }) => {
    const [optionDetails] = selectedOptions;
    const {
      doubleLine: senderName,
      data: { defaultReplyTo = '' } = {},
    } = optionDetails || {};

    /* Switching locales while the details form is active will trigger onSelect, which could result
     * in changes to the 'Reply to email' and 'From name' fields, making the form 'dirty' and
     * prompting the user to save changes. By checking to see if the onSelect call was actually due
     * to a change in value, we can eliminate this unintended call */
    if (selected !== emailData[fromEmailMediumDataKey]) {
      setCurrentMediumFields({
        [fromEmailMediumDataKey]: selected,
        [replyToMediumDataKey]: defaultReplyTo || selected,
        ...(senderName && { [fromNameMediumDataKey]: senderName }),
      });
    }
  };

  const memoizedSelectedOptions = useMemo(() => {
    return searchTerm
      ? emailSenders.filter(
          ({ doubleLine, label }) =>
            (label as string)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doubleLine?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : emailSenders;
  }, [emailSenders, searchTerm]);

  const inputFormFieldProps: SelectProps = {
    defaultValues: [emailData[fromEmailMediumDataKey] || ''],
    onSelect,
    ...(useDynamicSenders && {
      id: `${fromEmailMediumDataKey}-type-ahead-select-field`,
      'data-test': `${fromEmailMediumDataKey}-type-ahead-select-field`,
    }),
    ...(!useDynamicSenders && {
      id: `${fromEmailMediumDataKey}-select-field`,
      'data-test': `${fromEmailMediumDataKey}-select-field`,
      isDisabled: emailSenders.length === 0,
    }),
  };

  const basicFilterSelectProps: BasicSelectProps = {
    ...inputFormFieldProps,
    options: memoizedSelectedOptions,
    onSearchChange: (searchedItem: string | undefined) => setSearchTerm(searchedItem ?? ''),
    disableInternalFiltering: true,
  };

  const typeaheadSelectProps: TypeaheadBasicSelectProps = {
    ...inputFormFieldProps,
    options: emailSenders,
  };

  const InputFormField = useDynamicSenders ? (
    <TypeaheadSelect {...typeaheadSelectProps} isFullWidth />
  ) : (
    <BasicFilterSelect {...basicFilterSelectProps} isFullWidth />
  );

  const getEmailSenders = useCallback(() => {
    getMessageTypesAndSenders(projectId || 0, MessageMedium.Email)
      .then(({ data: { emailSenders: senders = [] } = {} }: SendersDataType) => {
        setEmailSenders(
          senders
            .filter(({ authorized }) => authorized)
            .map(({ email, senderName, defaultReplyTo }) => ({
              id: email,
              value: email,
              label: email,
              doubleLine: senderName,
              data: { defaultReplyTo },
            })),
        );
      })
      .catch((err: any) => {
        dispatch(addNegativeToast('Unable to fetch email senders list', err));
      });
  }, [projectId]);

  /**
   * Load all the sender information on component load.
   */
  useEffect(() => {
    getEmailSenders();
  }, []);

  return (
    <FormField
      data-testid={`${fromEmailMediumDataKey}-field`}
      helpText={t(getHelperText(isEditModeVisible, useDynamicSenders))}
      isHorizontalLayout
      labelText={t('label.fromEmail')}
      hasError={hasFromEmailError}
      errorText={fromEmailErrorText}
    >
      {isEditModeVisible ? (
        InputFormField
      ) : (
        <SettingsReadOnlyText>{emailData[fromEmailMediumDataKey] || ''}</SettingsReadOnlyText>
      )}
    </FormField>
  );
};
